import { useEffect, useRef, useState } from "react";

// JSON.stringify is a good-enough content hash for form snapshots. If a caller
// passes a value that can't be serialised (functions, cyclic refs), fall back
// to the raw value — the hook will still work; it just may over-fire.
function safeStringify(v) {
  try { return JSON.stringify(v); } catch { return String(v); }
}

/**
 * Debounced auto-save hook.
 *
 * Watches `value` and calls `save(value)` after `delay` ms of quiet time.
 * Skips the first render (so re-opening an editor doesn't immediately POST
 * back the same data). Exposes a UI-friendly status the caller can render.
 *
 * @param {any}      value    Any serialisable form snapshot. Change → re-arm.
 * @param {Function} save     async (value) => any. Called after debounce.
 * @param {object}   opts
 * @param {number}   opts.delay    Debounce window in ms (default 1200).
 * @param {boolean}  opts.enabled  When false, the hook idles.
 * @param {Function} opts.canSave  Optional predicate — called with `value`
 *                                 before firing `save`. Return falsy to skip.
 * @returns {{status:"idle"|"dirty"|"saving"|"saved"|"error", lastSavedAt:Date|null, saveNow:Function}}
 */
export default function useAutoSave(value, save, opts = {}) {
  const { delay = 1200, enabled = true, canSave } = opts;
  const [status, setStatus] = useState("idle");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const timer = useRef(null);
  const skipFirst = useRef(true);
  // Cache the latest save callback so the debounced timer always invokes the
  // freshest closure (avoids stale references to callers like `pageForm`).
  const saveRef = useRef(save);
  saveRef.current = save;
  const canSaveRef = useRef(canSave);
  canSaveRef.current = canSave;
  // Also cache `value` so the timer callback picks up the latest snapshot
  // regardless of how many renders happened between arming and firing.
  const valueRef = useRef(value);
  valueRef.current = value;
  // Serialised copy is used as the effect dependency so re-renders with the
  // SAME data (new object identity but identical content) don't reset the
  // debounce timer — otherwise it would never fire.
  const serialised = safeStringify(value);

  useEffect(() => {
    if (!enabled) return;
    if (skipFirst.current) {
      skipFirst.current = false;
      return;
    }
    setStatus("dirty");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const latest = valueRef.current;
      if (canSaveRef.current && !canSaveRef.current(latest)) {
        setStatus("idle");
        return;
      }
      try {
        setStatus("saving");
        await saveRef.current(latest);
        setStatus("saved");
        setLastSavedAt(new Date());
      } catch {
        setStatus("error");
      }
    }, delay);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [serialised, enabled, delay]);

  // Reset the "skip first render" latch whenever the hook is re-enabled.
  // Used when the dialog closes/opens between different entities.
  useEffect(() => {
    if (!enabled) {
      skipFirst.current = true;
      setStatus("idle");
    }
  }, [enabled]);

  const saveNow = async () => {
    if (timer.current) clearTimeout(timer.current);
    if (canSaveRef.current && !canSaveRef.current(value)) return;
    try {
      setStatus("saving");
      await saveRef.current(value);
      setStatus("saved");
      setLastSavedAt(new Date());
    } catch {
      setStatus("error");
    }
  };

  return { status, lastSavedAt, saveNow };
}
